<template>
  <div v-if="pageHelps.length" class="help-trigger flex items-center flex-shrink-0">
    <VaButton
      preset="plain"
      color="primary"
      icon="mso-help"
      class="help-button"
      :aria-label="t('help.pageModal.title')"
      @click="onTriggerClick"
    />

    <VaModal
      v-model="showPicker"
      class="help-modal"
      size="small"
      hide-default-actions
      mobile-fullscreen
      close-button
      no-outside-dismiss
    >
      <template #default>
        <h1 class="va-h5 mb-2">{{ t("help.pageModal.title") }}</h1>
        <p class="help-intro text-sm mb-4">{{ t("help.pageModal.intro") }}</p>

        <ul class="flex flex-col gap-2">
          <li v-for="help in pageHelps" :key="help.uniqueId">
            <button
              type="button"
              class="help-item w-full text-left px-3 py-2 rounded-lg flex items-center gap-2"
              @click="openHelp(help.uniqueId)"
            >
              <VaIcon name="mso-help" size="18px" color="primary" />
              <span>{{ getTranslatedLabel(help.title) }}</span>
            </button>
          </li>
        </ul>
      </template>

      <template #footer>
        <VaButton preset="secondary" @click="showPicker = false">
          {{ t("general.close") }}
        </VaButton>
      </template>
    </VaModal>

    <VaModal
      v-model="showContent"
      class="help-content-modal"
      max-width="900px"
      hide-default-actions
      mobile-fullscreen
      close-button
    >
      <template #default>
        <HelpContent v-if="selectedUniqueId" :unique-id="selectedUniqueId" show-title />
      </template>

      <template #footer>
        <VaButton preset="secondary" @click="showContent = false">
          {{ t("general.close") }}
        </VaButton>
      </template>
    </VaModal>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { storeToRefs } from "pinia"
import HelpContent from "@/pages/common/HelpContent.vue"
import type { HelpMenuItem } from "@/pages/admin/help/types"
import { getTranslatedLabel } from "@shared/helpers/utils"
import { useUserStore } from "@/stores/user-store"
import { useHelpStore } from "@/stores/help-store"

const { t } = useI18n()
const route = useRoute()
const { user } = storeToRefs(useUserStore())
const helpStore = useHelpStore()

const showPicker = ref(false)
const showContent = ref(false)
const selectedUniqueId = ref<string | null>(null)
const pageHelps = ref<HelpMenuItem[]>([])

let lastUserId: string | null = null

watch(
  [() => route.fullPath, () => user.value.uniqueId],
  async () => {
    const routeName = route.name ? (route.name as string) : null
    if (!routeName) {
      pageHelps.value = []
      return
    }
    // Reload the cached list when the user changes (e.g. impersonation); otherwise serve from cache.
    if (lastUserId !== null && lastUserId !== user.value.uniqueId) {
      await helpStore.reload()
    } else {
      await helpStore.ensureLoaded()
    }
    lastUserId = user.value.uniqueId

    pageHelps.value = helpStore.helpsForRoute(routeName)
  },
  { immediate: true },
)

function onTriggerClick(): void {
  if (pageHelps.value.length === 1) {
    openHelp(pageHelps.value[0].uniqueId)
  } else {
    showPicker.value = true
  }
}

function openHelp(uniqueId: string): void {
  showPicker.value = false
  selectedUniqueId.value = uniqueId
  showContent.value = true
}
</script>

<style scoped>
.help-button :deep(.va-icon) {
  font-size: 30px !important;
}

.help-content-modal :deep(.va-modal__close),
.help-modal :deep(.va-modal__close) {
  right: 1.5rem;
  z-index: 1;
}

.help-intro {
  color: var(--va-secondary);
}

.help-item {
  border: 1px solid var(--va-background-border);
}

.help-item:hover {
  background: var(--va-background-element);
}
</style>
